/**
 * Canonical data-size formatting utility — Spec 157 (FR-005).
 *
 * Single source of truth for rendering managed-data sizes (per-item, workspace,
 * capacity, and client rollups). Bytes are the canonical unit: the backend exposes
 * `totalSizeBytes`; per-item collectors store `storage_size_gb` (use {@link formatGb}).
 *
 * Convention (matches the spec's acceptance scenarios):
 *   - null / undefined  -> "—"   (size unavailable / not collected)
 *   - 0                 -> "0 B" (genuinely empty)
 *   - otherwise         -> "<n> <unit>" with 2 decimals, unit in B/KB/MB/GB/TB/PB
 *
 * NOTE: Several pre-Spec-157 local copies still exist (services/operations/platformService.ts,
 * modules/backup/hooks/useStorageMetrics.ts, useCleanupRuns.ts, services/snapshotsApi.ts).
 * Prefer this util for new code; those are a future consolidation sweep.
 */

const UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'] as const;
const BYTES_PER_GB = 1024 ** 3;

/**
 * Format a byte count to a human-readable string (B/KB/MB/GB/TB/PB, 2 decimals).
 * Returns "—" when the value is null/undefined (unavailable, not zero).
 */
export function formatBytes(bytes: number | null | undefined, decimals = 2): string {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) return '—';
  if (bytes === 0) return '0 B';
  if (bytes < 0) return '-' + formatBytes(-bytes, decimals);

  const dm = decimals < 0 ? 0 : decimals;
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const index = Math.min(i, UNITS.length - 1);

  return `${parseFloat((bytes / 1024 ** index).toFixed(dm))} ${UNITS[index]}`;
}

/**
 * Format a size given in gigabytes (e.g. dbo.lakehouses.storage_size_gb) by converting
 * to bytes first, so a single B/KB/MB/GB/TB/PB scale is applied consistently.
 */
export function formatGb(gb: number | null | undefined, decimals = 2): string {
  if (gb === null || gb === undefined || Number.isNaN(gb)) return '—';
  return formatBytes(gb * BYTES_PER_GB, decimals);
}
